import { $elementChild } from "@grindex-common/helpers/elements";
import { showElement, hideElement } from "@grindex-common/helpers/domManipulation";
import { isString } from "@grindex-client/common/js/helpers/typeHelpers";

type SearchResultData = {
	type?: string;
	imageUrl?: string;
	imageAltText: string;
	description?: string;
	iconClass?: string;
	discontinued?: boolean;
	discontinuedText: string;
	url: string;
	hitTrackingContentId: string;
	removeQueryString?: boolean;
	readMoreText?: string;
	brandName: string;
	extraHeading?: string;
	brandLogoUrl?: string;
	compareLabel?: string;
	heading: string;
};

export class SearchResultParser {
	brandImageResizeString: string;
	resultImageResizeString: string;
	selectedFacets: string[] = [];

	constructor() {
		this.brandImageResizeString = "?width=270&height=270&rmode=boxpad&bgcolor=fff&format=webp";
		this.resultImageResizeString = "?width=270&height=270&rmode=boxpad&bgcolor=fff&format=webp";
	}

	renderResult($container: HTMLElement, data: SearchResultData) {
		const $readMore = $elementChild($container, "read-more") as HTMLElement;
		const $description = $elementChild($container, "description") as HTMLElement;
		const $resultImageContainer = $elementChild($container, "image-container") as HTMLElement;
		const $facetContainer = document.querySelector("[data-component-name='searchfilterpanel']");
		this.selectedFacets = [];

		if ($facetContainer) {
			const checkedFacets = [...$facetContainer.querySelectorAll<HTMLInputElement>("input:checked")];
			for (const element of checkedFacets) {
				const id = element.dataset["id"];
				if (isString(id)) {
					this.selectedFacets.push(id);
				}
			}
		}

		// Only run this if the data isn't populated
		if ($readMore.childNodes.length === 0) {
			if (data.type) {
				$container.classList.add(data.type.replace(" ", "-").toLowerCase());
			}

			if (data.imageUrl) {
				const $imageLink = this.getResultImageLink(
					{
						url: data.url,
						hitTrackingContentId: data.hitTrackingContentId,
						imageUrl: data.imageUrl,
						imageAltText: data.imageAltText,
						imageFormat: this.resultImageResizeString,
						isVideo: data.type === "Video",
						removeQueryString: data.removeQueryString ?? false,
					},
					this.resultImageResizeString,
					$resultImageContainer,
				);
				this.renderImage($container, $imageLink);
				$description.innerHTML = data.description;
			} else if (data.iconClass) {
				this.replaceImageWithIcon($resultImageContainer, data.iconClass);
			} else {
				// No Image or Icon
				this.hideImage($container);
			}

			if (data.description) {
				$description.innerHTML = data.description;
			}

			if ($readMore) {
				const $readMoreLink = this.generateLinkElement(data);
				$readMore.appendChild($readMoreLink);
			}

			this.renderTitle($container, data);

			this.renderBrandData($container, data);

			this.renderExtraHeading($container, data);

			this.renderProductData($container, data);

			this.renderDiscontinuedElement($container, data);
		}
	}

	replaceImageWithIcon($container: HTMLElement, iconClass: string) {
		$container.classList.add("doc-icon", `${iconClass}-big`);
	}

	renderDiscontinuedElement($container: HTMLElement, data: SearchResultData) {
		if (data.discontinued) {
			const $discontinued = $elementChild($container, "discontinued") as HTMLElement;
			$discontinued.innerText = data.discontinuedText;
		}
	}

	renderVideoExtras($container: HTMLElement) {
		const $videoOverlaySpan = document.createElement("span");
		$videoOverlaySpan.className = "video-container__placeholder";
		$container.appendChild($videoOverlaySpan);
	}

	createImage(imageUrl: string, altText: string, imageResizeString: string | null, $imageContainer: HTMLElement) {
		const src = `${imageUrl}${imageResizeString ? imageResizeString : ""}`;

		const img = new Image() as HTMLImageElement & { load: string };

		img.src = src;
		img.alt = altText;
		img.load = "lazy";
		img.onerror = () => {
			$imageContainer.remove();
		};

		return img;
	}

	renderType($container: HTMLElement, data: SearchResultData) {
		const $type = $elementChild($container, "type") as HTMLElement;

		$type.innerText = data.type;
	}

	removeImageContainer($container: HTMLElement) {
		$container.remove();
	}

	createLink(url: string, hitTrackingContentId: string) {
		const imageLink = document.createElement("a");
		imageLink.href = url;
		imageLink.target = "_self";
		imageLink.setAttribute("xyl-hittrackingid", hitTrackingContentId);
		return imageLink;
	}

	generateLinkElement(data: SearchResultData) {
		const $readMoreLink = document.createElement("a");
		const parsedUrl = data.removeQueryString === true ? data.url : this.appendFacets(data.url);

		$readMoreLink.href = parsedUrl;
		$readMoreLink.target = "_self";

		if (data.readMoreText) {
			$readMoreLink.innerText = data.readMoreText;
		}

		return $readMoreLink;
	}

	appendFacets(url: string) {
		let parsedUrl = url;

		if (this.selectedFacets.length > 0) {
			parsedUrl += "?facetids=";
			parsedUrl += this.selectedFacets.join("&facetids=");
		}

		return parsedUrl;
	}

	renderExtraHeading($container: HTMLElement, data: SearchResultData) {
		const $extraHeading = $elementChild($container, "extra-heading") as HTMLElement;
		if (data.brandName) {
			$extraHeading.innerText = `${data.brandName}`;
		} else if (data.extraHeading) {
			$extraHeading.innerText = `${data.extraHeading}`;
		}
	}

	renderBrandData($container: HTMLElement, data: SearchResultData) {
		if (data.brandLogoUrl) {
			const $brandImageContainer = $elementChild($container, "brand-logo") as HTMLElement;

			if ($brandImageContainer) {
				$brandImageContainer.classList.remove("hidden");

				const $brandImage = this.createImage(data.brandLogoUrl, data.brandName, null, $brandImageContainer);

				$brandImageContainer.appendChild($brandImage);
			}
		}
	}

	renderProductData($container: HTMLElement, data: SearchResultData) {
		$container.classList.add("result-Product");
		if (data.compareLabel) {
			const $compareLabel = $elementChild($container, "compare-label") as HTMLElement;
			const $compareContainer = $elementChild($container, "compare-container") as HTMLElement;

			$compareLabel.innerText = data.compareLabel;
			if (!document.body.classList.contains("brand-page")) {
				(showElement as (element: HTMLElement) => void)($compareContainer);
			}
		}
	}

	renderImage($root: HTMLElement, $image: HTMLElement) {
		const $imageContainer = $elementChild($root, "image-container") as HTMLElement;
		if ($imageContainer) {
			$imageContainer.appendChild($image);
		}
	}

	hideImage($root: HTMLElement) {
		const $imageContainer = $elementChild($root, "image-container") as HTMLElement;
		if ($imageContainer) {
			(hideElement as (element: HTMLElement) => void)($imageContainer);
		}
	}

	renderTitle($container: HTMLElement, data: SearchResultData) {
		const $titleHeading = $elementChild($container, "title") as HTMLElement;
		const url = data.removeQueryString === true ? data.url : this.appendFacets(data.url);
		const $titleLink = this.createLink(url, data.hitTrackingContentId);

		$titleHeading.dataset["friendlyName"] = data.heading;

		if ($titleHeading.querySelectorAll("a").length === 0) {
			$titleLink.innerText = data.heading;
			$titleHeading.appendChild($titleLink);
		}
	}

	getResultImageLink(
		data: {
			url: string;
			hitTrackingContentId: string;
			imageUrl: string;
			imageAltText: string;
			imageFormat: string;
			isVideo: boolean;
			removeQueryString: boolean;
		},
		imageFormat: string,
		$imageContainer: HTMLElement,
	) {
		const url = data.removeQueryString ? data.url : this.appendFacets(data.url);
		const $imageLink = this.createLink(url, data.hitTrackingContentId);
		const img = this.createImage(data.imageUrl, data.imageAltText, imageFormat, $imageContainer);

		$imageLink.appendChild(img);

		if (data.isVideo) {
			this.renderVideoExtras($imageLink);
		}

		return $imageLink;
	}
}
